/**
 * THE CORE PROJECT JOURNEY — what the screens along it may claim.
 *
 * Three defects on the two screens every contractor passes through first, all
 * of them the same mistake wearing different clothes: **a read that did not
 * happen rendered as a fact about the business.**
 *
 *   ProjectsPage.jsx:147   a failed GET /projects is caught, a toast fires,
 *                          and `projects` stays []. The page then renders
 *                          "No projects found — Get started by creating your
 *                          first project". A contractor with forty live
 *                          projects, on a bad connection, is told they have
 *                          none and invited to create their first.
 *
 *   ProjectsPage.jsx:300   `projects.filter(...)` runs on whatever the
 *                          backend sent. A non-array throws during render —
 *                          a white screen, not a refusal.
 *
 *   ProjectDetailPage:784  `Work Breakdown Structure ({activities.length})`.
 *                          A failed activities read prints "(0)": this
 *                          project has no work breakdown.
 *
 * The distinction this module exists to hold:
 *
 *   THE REQUEST FAILED        we do not know what exists
 *     is not
 *   THE PAYLOAD IS UNREADABLE we were told something we cannot parse
 *     is not
 *   THE ANSWER IS ZERO        we know, and the answer is none
 *
 * Only the third is a fact about the contractor's business. The first two are
 * facts about this request, and a screen that renders them as the third is
 * lying about a jobsite.
 *
 * Everything here is pure. It reads what the backend sent and decides what may
 * be claimed from it; it derives no construction fact and no authority.
 */

/* ------------------------------------------------------------------ *
 * COLLECTION READS
 * ------------------------------------------------------------------ */

export const REFUSED = 'REFUSED';
export const UNREADABLE = 'UNREADABLE';
export const READ = 'READ';

/**
 * Read a collection the backend returns as a bare JSON array.
 *
 * `GET /projects` (routes/projects_v2.py:120) is `response_model=List[...]`,
 * so the wire shape is an array and not an envelope. Anything else is a
 * contract violation and is refused rather than coerced.
 *
 * `requestFailed` is passed by the caller because only the caller knows
 * whether the HTTP call threw. It is deliberately not inferred from a null
 * payload: a route that legitimately returns `null` and a route that blew up
 * are different, and guessing between them is the defect.
 */
export const readCollection = (payload, { requestFailed = false, rowIsReadable = null } = {}) => {
  if (requestFailed) {
    return {
      state: REFUSED,
      items: [],
      unreadableRows: 0,
      reason: 'The request did not complete, so what exists is unknown.',
    };
  }
  if (!Array.isArray(payload)) {
    return {
      state: UNREADABLE,
      items: [],
      unreadableRows: 0,
      reason: 'The response was not a list. MALFORMED IS NOT EMPTY.',
    };
  }
  if (typeof rowIsReadable !== 'function') {
    return { state: READ, items: payload.slice(), unreadableRows: 0, reason: null };
  }
  const items = [];
  let unreadableRows = 0;
  for (const row of payload) {
    if (rowIsReadable(row)) items.push(row);
    else unreadableRows += 1;
  }
  // An unreadable ROW is counted on screen rather than silently dropped, so
  // "12 projects" when the backend sent 13 cannot happen quietly.
  return { state: READ, items, unreadableRows, reason: null };
};

/** A project row the list can actually render and navigate to. */
export const projectRowIsReadable = (row) =>
  Boolean(row) &&
  typeof row === 'object' &&
  !Array.isArray(row) &&
  typeof row.id === 'string' &&
  row.id.length > 0 &&
  typeof row.name === 'string' &&
  row.name.length > 0;

export const readProjectList = (payload, options = {}) =>
  readCollection(payload, { ...options, rowIsReadable: projectRowIsReadable });

/* ------------------------------------------------------------------ *
 * WHAT A COLLECTION READ IS ALLOWED TO SAY
 * ------------------------------------------------------------------ */

export const NOT_ESTABLISHED_LABEL = 'Not established';

/**
 * A count is printable only when the read succeeded.
 *
 * This is the whole of the `Work Breakdown Structure (0)` repair: zero is
 * printed when zero was read, and nothing is printed when nothing was.
 */
export const collectionCountLabel = (read) =>
  read && read.state === READ ? String(read.items.length) : NOT_ESTABLISHED_LABEL;

/** True only when the backend actually answered and the answer was none. */
export const isEstablishedEmpty = (read) =>
  Boolean(read) && read.state === READ && read.items.length === 0 && read.unreadableRows === 0;

/**
 * The one decision the project list screen makes.
 *
 * Kept here rather than in the page so that a test can state the rule without
 * mounting a 800-line component, and so a mutation has one line to attack.
 */
export const projectListView = (read) => {
  if (!read || read.state === REFUSED) {
    return {
      kind: 'REFUSED',
      // NOTE the absence: this says nothing about how many projects exist,
      // because we do not know. Offering "create your first project" here is
      // the defect.
      headline: 'The project list could not be read',
      detail:
        'This says nothing about how many projects exist — the request did not ' +
        'complete. Nothing has been created, changed or removed.',
      retryable: true,
      showCreateFirst: false,
      projects: [],
      unreadableRows: 0,
    };
  }
  if (read.state === UNREADABLE) {
    return {
      kind: 'UNREADABLE',
      headline: 'The project list came back in a shape OMNΛS cannot read',
      detail:
        'Rendering it as an empty list would claim this organisation has no ' +
        'projects. MALFORMED IS NOT EMPTY.',
      retryable: true,
      showCreateFirst: false,
      projects: [],
      unreadableRows: 0,
    };
  }
  if (read.items.length === 0) {
    return {
      kind: 'EMPTY',
      headline: 'No projects yet',
      detail: 'The list was read and it is empty. Create the first project to begin.',
      retryable: false,
      // The ONLY state in which this invitation is truthful.
      showCreateFirst: true,
      projects: [],
      unreadableRows: read.unreadableRows,
    };
  }
  return {
    kind: 'POPULATED',
    headline: null,
    detail: null,
    retryable: false,
    showCreateFirst: false,
    projects: read.items,
    unreadableRows: read.unreadableRows,
  };
};

/**
 * What to say about rows that came back but could not be rendered.
 *
 * Returns null when there are none, so a caller can render nothing without
 * asking a second question.
 */
export const unreadableRowsNotice = (read) => {
  const n = read && read.state === READ ? read.unreadableRows : 0;
  if (!n) return null;
  return n === 1
    ? '1 project in this response could not be read and is not listed.'
    : `${n} projects in this response could not be read and are not listed.`;
};

/* ------------------------------------------------------------------ *
 * THE PROJECT-SWITCH INVARIANT
 * ------------------------------------------------------------------ */

/**
 * PROJECT A REQUEST PENDING -> USER SWITCHES TO B -> A RESOLVES LAST.
 * NO PROJECT A TRUTH MAY RENDER UNDER PROJECT B.
 *
 * `ProjectDetailPage.fetchProjectData` runs from an effect keyed on
 * `projectId` with no cleanup and no sequencer, and it performs six
 * sequential awaits. Switching projects mid-flight therefore lets the slower,
 * older response call `setProject` last and paint Project A's name, budget and
 * work breakdown under Project B's URL.
 *
 * This is the smallest honest guard: the caller stamps the id it asked about,
 * and every write is dropped unless that id is still the one on screen. It is
 * an ownership test, not a timestamp — two requests for the SAME project may
 * resolve in any order without harm, and a stale one for a DIFFERENT project
 * is never allowed to win.
 */
export const createProjectScope = (projectId) => ({
  projectId,
  owns: (currentId) => currentId === projectId,
});

/** True when a response for `responseFor` may be written while viewing `current`. */
export const mayApply = (responseFor, current) => responseFor === current;
